import React from "react";
import PropTypes from "prop-types";
import {
  Paper,
  Box,
  Typography,
  Divider,
  Grid,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Button,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import CakeIcon from "@mui/icons-material/Cake";
import WcIcon from "@mui/icons-material/Wc";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import WarningIcon from "@mui/icons-material/Warning";
import AddIcon from "@mui/icons-material/Add";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

// Whole years since a YYYY-MM-DD date of birth, or null if it can't be parsed
export function ageFromDob(dob, today = new Date()) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dob || "");
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  let age = today.getFullYear() - year;
  const beforeBirthday = today.getMonth() + 1 < month || (today.getMonth() + 1 === month && today.getDate() < day);
  if (beforeBirthday) age -= 1;
  return age >= 0 ? age : null;
}

function PatientOverviewBanner({
  patient,
  patientSummary,
  nkdaChecked,
  onNkdaChange,
  onAddAllergy,
  onAddEncounter,
  onAddAppointment,
  onAddInsurance,
  onAddProgramEnrollment,
  onCreateCarePlan,
  onOpenDeviceDialog,
}) {
  return (
    <Paper
      sx={{
        p: 3,
        mb: 2,
        background:
          "linear-gradient(135deg, rgba(121, 184, 201, 0.05) 0%, rgba(215, 109, 135, 0.05) 100%)",
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h5" display="flex" alignItems="center" gap={1.5}>
          <PersonIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" component="span" fontWeight={500}>
              {patient.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              MRN: {patient.mrn}
            </Typography>
          </Box>
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={2}>
          <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 1.5 }}>
            <PersonIcon fontSize="small" color="primary" />
            <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
              DEMOGRAPHICS
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
            <CakeIcon fontSize="small" color="primary" />
            <Typography variant="body2">
              DOB: {patient.dob}
              {ageFromDob(patient.dob) !== null && ` (${ageFromDob(patient.dob)}y)`}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
            <WcIcon fontSize="small" color="secondary" />
            <Typography variant="body2">Sex: {patient.sex}</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
            <PhoneIcon fontSize="small" color="info" />
            <Typography variant="body2">{patient.phone}</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
            <EmailIcon fontSize="small" color="error" />
            <Typography variant="body2">{patient.email}</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
            <HomeIcon fontSize="small" color="success" />
            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
              {patient.address}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <WarningIcon fontSize="small" color="warning" />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                ALLERGIES
              </Typography>
            </Box>
            <IconButton 
              size="small" 
              onClick={onAddAllergy}
              data-testid="add-allergy-button"
              aria-label="Add new allergy"
              data-action="add-allergy"
            >
              <AddIcon aria-label="Add new allergy" />
            </IconButton>
          </Box>
          {patientSummary?.allergies?.length > 0 ? (
            patientSummary.allergies.slice(0, 2).map((allergy) => (
              <Box key={allergy.id} mb={0.5}>
                <Box display="flex" gap={0.5} alignItems="center">
                  <Typography variant="body2" fontWeight={500}>
                    {allergy.allergen}
                  </Typography>
                  <Chip
                    label={allergy.severity}
                    size="small"
                    color={allergy.severity === "severe" ? "error" : "warning"}
                    variant="outlined"
                    sx={{ height: 16, fontSize: "0.65rem" }}
                    data-field="allergy-severity"
                    data-severity={allergy.severity}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {allergy.reaction}
                </Typography>
              </Box>
            ))
          ) : (
            <FormControlLabel
              control={
                <Checkbox 
                  checked={nkdaChecked} 
                  onChange={onNkdaChange} 
                  size="small"
                  data-testid="nkda-checkbox"
                  aria-label="No Known Drug Allergies"
                  data-field="nkda"
                />
              }
              label={<Typography variant="body2">NKDA</Typography>}
              sx={{ m: 0 }}
            />
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <LocalHospitalIcon fontSize="small" color="info" />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                ENCOUNTERS
              </Typography>
            </Box>
            <IconButton 
              size="small" 
              onClick={onAddEncounter}
              data-testid="add-encounter-button"
              aria-label="Add new encounter"
              data-action="add-encounter"
            >
              <AddIcon aria-label="Add new encounter" />
            </IconButton>
          </Box>
          {patientSummary?.encounters?.length > 0 ? (
            patientSummary.encounters.slice(0, 2).map((encounter) => (
              <Box key={encounter.id} mb={0.5}>
                <Typography variant="body2" fontWeight={500}>
                  {encounter.encounter_type}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {encounter.encounter_date} • Dr. {encounter.provider}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No encounters
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <CalendarTodayIcon fontSize="small" color="success" />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                APPOINTMENTS
              </Typography>
            </Box>
            <IconButton 
              size="small" 
              onClick={onAddAppointment}
              data-testid="add-appointment-button"
              aria-label="Add new appointment"
              data-action="add-appointment"
            >
              <AddIcon aria-label="Add new appointment" />
            </IconButton>
          </Box>
          {patientSummary?.appointments?.length > 0 ? (
            patientSummary.appointments.slice(0, 2).map((apt) => (
              <Box key={apt.id} mb={0.5}>
                <Box display="flex" gap={0.5} alignItems="center">
                  <Typography variant="body2" fontWeight={500}>
                    {apt.appointment_type}
                  </Typography>
                  <Chip label={apt.status} size="small" variant="outlined" sx={{ height: 16, fontSize: "0.65rem" }} 
                    data-field="appointment-status"
                    data-status={apt.status}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {apt.scheduled_at ? new Date(apt.scheduled_at).toLocaleString() : "N/A"}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No appointments
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              INSURANCE
            </Typography>
            <IconButton 
              size="small" 
              onClick={onAddInsurance}
              data-testid="add-insurance-button"
              aria-label="Add insurance information"
              data-action="add-insurance"
            >
              <AddIcon aria-label="Add insurance information" />
            </IconButton>
          </Box>
          {patientSummary?.insurance?.length > 0 ? (
            patientSummary.insurance.slice(0, 2).map((ins) => (
              <Box key={ins.id} mb={0.5}>
                <Typography variant="body2" fontWeight={500}>
                  {ins.insurance_company}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {ins.policy_number} {ins.is_primary && "• Primary"}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No insurance
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              PROGRAMS
            </Typography>
            <IconButton 
              size="small" 
              onClick={onAddProgramEnrollment}
              data-testid="add-program-enrollment-button"
              aria-label="Add program enrollment"
              data-action="add-program-enrollment"
            >
              <AddIcon aria-label="Add program enrollment" />
            </IconButton>
          </Box>
          {patientSummary?.program_enrollments?.length > 0 ? (
            patientSummary.program_enrollments.slice(0, 2).map((enrollment) => (
              <Box key={enrollment.id} mb={0.5}>
                <Typography variant="body2">{enrollment.program_name}</Typography>
                <Chip label={enrollment.status} size="small" variant="outlined" sx={{ height: 16, fontSize: "0.65rem" }}
                  data-field="program-status"
                  data-status={enrollment.status}
                />
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No enrollments
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              CARE PLANS
            </Typography>
            <IconButton 
              size="small" 
              onClick={onCreateCarePlan}
              data-testid="add-care-plan-button"
              aria-label="Create new care plan"
              data-action="create-care-plan"
            >
              <AddIcon aria-label="Create new care plan" />
            </IconButton>
          </Box>
          {patientSummary?.care_plans?.length > 0 ? (
            patientSummary.care_plans.slice(0, 2).map((plan) => (
              <Box key={plan.id} mb={0.5}>
                <Typography variant="body2">{plan.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {plan.start_date}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No care plans
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <DevicesIcon 
                fontSize="small" 
                color="secondary" 
                aria-label="Device orders section"
                data-testid="devices-section-icon"
                data-category="devices"
              />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                DEVICES
              </Typography>
            </Box>
            <Tooltip title="Order devices for remote monitoring">
              <IconButton 
                size="small" 
                onClick={onOpenDeviceDialog}
                data-testid="add-device-order-button"
                aria-label="Order remote monitoring device"
                data-action="order-device"
              >
                <AddIcon 
                  aria-label="Order remote monitoring device" 
                  data-testid="add-device-icon"
                  data-action="order-device"
                  data-category="devices"
                />
              </IconButton>
            </Tooltip>
          </Box>
          {patientSummary?.rpm_device_orders?.length > 0 ? (
            [...patientSummary.rpm_device_orders]
              .sort((a, b) => new Date(b.order_date || 0) - new Date(a.order_date || 0))
              .slice(0, 2)
              .map((order, index) => (
              <Box key={index} mb={0.5}>
                <Box display="flex" gap={0.5} alignItems="center">
                  <Typography variant="body2" fontWeight={500}>
                    {order.device_type}
                  </Typography>
                  <Chip
                    label={order.status}
                    size="small"
                    variant="outlined"
                    sx={{ height: 16, fontSize: "0.65rem" }}
                    data-field="device-status"
                    data-status={order.status}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {order.tracking_number || "Not shipped"}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No devices
            </Typography>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
}

PatientOverviewBanner.propTypes = {
  patient: PropTypes.object.isRequired,
  patientSummary: PropTypes.shape({
    age: PropTypes.number,
    allergies: PropTypes.array,
    encounters: PropTypes.array,
    appointments: PropTypes.array,
    insurance: PropTypes.array,
    program_enrollments: PropTypes.array,
    care_plans: PropTypes.array,
    rpm_device_orders: PropTypes.array,
  }),
  nkdaChecked: PropTypes.bool.isRequired,
  onNkdaChange: PropTypes.func.isRequired,
  onAddAllergy: PropTypes.func.isRequired,
  onAddEncounter: PropTypes.func.isRequired,
  onAddAppointment: PropTypes.func.isRequired,
  onAddInsurance: PropTypes.func.isRequired,
  onAddProgramEnrollment: PropTypes.func.isRequired,
  onCreateCarePlan: PropTypes.func.isRequired,
  onOpenDeviceDialog: PropTypes.func.isRequired,
};

PatientOverviewBanner.defaultProps = {
  patientSummary: null,
};

export default PatientOverviewBanner;
